import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { ChevronRight, ChevronLeft, X, Sparkles, Play } from "lucide-react";
import { IsaiahMascotCore } from "./isaiah";
import axios from "axios";
import { API, useApp } from "../context";
import { TOUR_STEPS, STORAGE_KEY } from "./platformTour/tourSteps";
import { SpotlightOverlay } from "./platformTour/SpotlightOverlay";
import { CTAModal } from "./platformTour/CTAModal";

// MODULE-LEVEL DEDUPE: survives StrictMode mount-unmount-remount cycles.
// React.StrictMode (dev) and certain auth state transitions cause the tour
// component to mount twice in rapid succession on the homepage, which
// previously produced two audible TTS plays of the same step, the
// "two voices echoing right behind each other" the user heard.
const _tourSpoken = { sig: "", at: 0 };
// First-time users should see the Welcome Tour immediately on entry. Since the
// tour spotlights homepage elements, we bring a brand-new user to "/" ONCE so
// it can run right away rather than only firing when they later tap "Home".
let _autoNavNudged = false;

/* ---- Main Tour Component ---- */
const PlatformTour = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { loginAsDemo, user } = useApp();
  const isHomePage = location.pathname === "/";
  // Gate the entire tour (auto-start AND the persistent "Take a Tour"
  // launcher button) on having an authenticated user. Public visitors
  // (pre-signup) should NOT hear Isaiah's voice or see tour chrome -
  // they're meant to browse the marketing homepage and click Sign Up
  // when they're ready.
  const isAuthenticated = !!user && !user?.isDemo;
  const isGamePage = location.pathname.startsWith("/learn/") || location.pathname.startsWith("/arcade/")
    || location.pathname === "/learn" || location.pathname === "/arcade"
    || location.pathname === "/adventures" || location.pathname === "/create"
    || location.pathname === "/art-studio" || location.pathname.startsWith("/art-studio/");
  const [active, setActive] = useState(false);
  const [step, setStep] = useState(0);
  // Per-account "tour already seen" flag from the server. null = unknown yet
  // (we wait for the answer before auto-starting so the tour NEVER flashes on
  // a returning account after refresh/re-login/new device).
  const [serverSeen, setServerSeen] = useState(null);
  // Admin-controlled tour config (DB-backed): global on/off + per-step text.
  const [tourEnabled, setTourEnabled] = useState(true);
  const [stepOverrides, setStepOverrides] = useState({});
  const [stepOrder, setStepOrder] = useState(null); // array of original indices, or null
  const [extraSteps, setExtraSteps] = useState([]); // admin-added text-only boxes
  const [previewMode, setPreviewMode] = useState(false);
  useEffect(() => {
    // Preview mode, admin tapped "Preview tour" with unsaved edits. Applies
    // the draft config from sessionStorage and force-starts on the homepage,
    // ignoring the enabled/completed gates. Cleared when the tour ends.
    let previewApplied = false;
    try {
      const raw = sessionStorage.getItem("iw_tour_preview");
      if (raw) {
        const p = JSON.parse(raw);
        const map = {};
        (p.steps || []).forEach((s) => { if (typeof s.index === "number") map[s.index] = s; });
        setStepOverrides(map);
        setStepOrder(Array.isArray(p.order) && p.order.length ? p.order : null);
        setExtraSteps(Array.isArray(p.extra_steps) ? p.extra_steps : []);
        setTourEnabled(true);
        setPreviewMode(true);
        previewApplied = true;
      }
    } catch { /* ignore */ }
    if (previewApplied) return;
    let alive = true;
    axios.get(`${API}/tour/config`).then((r) => {
      if (!alive) return;
      setTourEnabled(r.data?.enabled !== false);
      const map = {};
      (r.data?.steps || []).forEach((s) => {
        if (typeof s.index === "number") map[s.index] = s;
      });
      setStepOverrides(map);
      setStepOrder(Array.isArray(r.data?.order) && r.data.order.length ? r.data.order : null);
      setExtraSteps(Array.isArray(r.data?.extra_steps) ? r.data.extra_steps : []);
    }).catch(() => {});
    return () => { alive = false; };
  }, []);
  // Merge admin text overrides (title/message only) over the code-defined
  // step structure, apply the admin-chosen order, then append any admin-added
  // text-only steps (centered, no spotlight).
  const mergedSteps = useMemo(() => {
    const base = TOUR_STEPS.map((s, i) => {
      const o = stepOverrides[i];
      if (!o) return s;
      return {
        ...s,
        ...(o.title ? { title: o.title } : {}),
        ...(o.message ? { message: o.message } : {}),
      };
    });
    let seq = base;
    if (stepOrder && stepOrder.length) {
      const ordered = stepOrder.map((i) => base[i]).filter(Boolean);
      base.forEach((s, i) => { if (!stepOrder.includes(i)) ordered.push(s); });
      seq = ordered;
    }
    const extras = (extraSteps || []).map((e) => ({
      title: e.title || "New step",
      message: e.message || "",
      targets: [],
      position: "center",
      pose: "waving",
    }));
    return [...seq, ...extras];
  }, [stepOverrides, stepOrder, extraSteps]);
  const [targetRects, setTargetRects] = useState([]);
  const [showCTA, setShowCTA] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [voiceEnabled, setVoiceEnabled] = useState(() => {
    try { return localStorage.getItem("isaiah_tour_voice") !== "off"; } catch { return true; }
  });
  const tooltipRef = useRef(null);
  const audioRef = useRef(null);          // HTMLAudioElement for Isaiah voice
  const audioCacheRef = useRef(new Map()); // text → data URL (session cache)
  const speakTokenRef = useRef(0);         // monotonic token, lets pending speak() calls cancel themselves
  const lastSpokenSigRef = useRef({ sig: "", at: 0 }); // de-dup guard against React.StrictMode double-invocation

  // Voice narration, Isaiah brand voice via ElevenLabs (cached in Mongo + in-memory).
  // Falls back to Web Speech API if the TTS endpoint fails.
  //
  // AUTOPLAY UNLOCK: we create the <audio> element synchronously while we still
  // have the user-gesture permission (click), then swap its .src once the TTS
  // response comes back. If we await the network call first, the browser drops
  // the user-gesture grant and silently blocks audio.play().
  //
  // RACE-CONDITION GUARD: each speak() call claims a new token. If the token
  // advances (user clicked Next, or the tour ended) while we're awaiting the
  // network, we bail out instead of playing stale audio that would overlap
  // with the new step's voice.
  const speak = useCallback(async (text) => {
    if (!voiceEnabled || !text) return;

    // DEDUPE GUARD (instance-level + module-level). React.StrictMode (dev)
    // double-invokes effects on mount, and even the production homepage
    // briefly re-mounts the tour during auth state hydration. Without
    // these guards we get the 2-voice "echo" the user reported.
    const now = Date.now();
    if (lastSpokenSigRef.current.sig === text && (now - lastSpokenSigRef.current.at) < 1500) {
      return;
    }
    if (_tourSpoken.sig === text && (now - _tourSpoken.at) < 6000) {
      return;
    }
    lastSpokenSigRef.current = { sig: text, at: now };
    _tourSpoken.sig = text;
    _tourSpoken.at = now;

    // GLOBAL VOICE LOCK, if another voice element (HomeAutoWelcome, lesson
    // narration) is currently playing, wait for it to finish before starting
    // tour narration. Prevents the "voices stacked on top of each other"
    // bug the user reported on the homepage.
    if (window.__iw_voice_lock && window.__iw_voice_lock !== "platform-tour") {
      // Defer until the other voice releases the lock (poll up to 25s)
      let waited = 0;
      while (window.__iw_voice_lock && window.__iw_voice_lock !== "platform-tour" && waited < 25000) {
        await new Promise((res) => setTimeout(res, 250));
        waited += 250;
      }
    }

    // Claim a token and bump all prior in-flight speaks so they abort themselves.
    const myToken = ++speakTokenRef.current;

    // Stop any currently playing audio
    try {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.src = "";
      }
      if ("speechSynthesis" in window) window.speechSynthesis.cancel();
    } catch {}

    // 1) Create the Audio element NOW while we still have the gesture permission.
    //    We load a 1-frame silent placeholder so the autoplay grant sticks.
    const audio = new Audio();
    audio.volume = 0.95;
    audioRef.current = audio;
    // Short silent MP3 (primes the element, doesn't audibly play anything)
    audio.src = "data:audio/mp3;base64,SUQzBAAAAAABEVRYWFgAAAAtAAADY29tbWVudABCaWdTb3VuZEJhbmsuY29tIC8gTGFTb25vdGhlcXVlLm9yZwBURU5DAAAAHQAAA1N3aXRjaCBQbHVzIMKpIE5DSCBTb2Z0d2FyZQBUSVQyAAAABgAAAzIyMzUAVFNTRQAAAA8AAANMYXZmNTcuODMuMTAwAAAAAAAAAAAAAAD/80DEAAAAA0gAAAAATEFNRTMuMTAwVVVVVVVVVVVVVUxBTUUzLjEwMFVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVV";
    // Kick the element, this "claims" the autoplay permission from the click
    try { await audio.play(); audio.pause(); } catch { /* okay, user has sound off */ }
    if (myToken !== speakTokenRef.current) return;  // a newer speak/stop superseded us

    try {
      // Session-level in-memory cache to avoid network round-trip on the same step
      let dataUrl = audioCacheRef.current.get(text);
      if (!dataUrl) {
        const r = await axios.post(`${API}/tts/tour`, { text }, { timeout: 15000 });
        if (myToken !== speakTokenRef.current) return;  // superseded during fetch
        dataUrl = r.data?.audio_url;
        if (dataUrl) audioCacheRef.current.set(text, dataUrl);
      }
      if (!dataUrl) throw new Error("No audio URL");
      if (myToken !== speakTokenRef.current) return;  // final abort check

      // 2) Swap src on the SAME element (keeps autoplay grant) and play.
      audio.src = dataUrl;
      window.__iw_voice_lock = "platform-tour";
      audio.onended = () => { if (window.__iw_voice_lock === "platform-tour") delete window.__iw_voice_lock; };
      audio.onpause = () => { if (window.__iw_voice_lock === "platform-tour" && audio.ended) delete window.__iw_voice_lock; };
      try {
        await audio.play();
      } catch (playErr) {
        if (myToken !== speakTokenRef.current) return;  // was stopped during play-await
        // Autoplay blocked, most common on first page load before ANY user
        // gesture. Wait for the next click/tap/keypress anywhere on the page
        // and retry silently. This is why voice "suddenly works" after the
        // mute button is toggled: that click IS the gesture we need.
        const retry = () => {
          document.removeEventListener("pointerdown", retry, true);
          document.removeEventListener("keydown", retry, true);
          document.removeEventListener("touchstart", retry, true);
          if (myToken === speakTokenRef.current && audioRef.current === audio) {
            audio.play().catch(() => {});
          }
        };
        document.addEventListener("pointerdown", retry, true);
        document.addEventListener("keydown", retry, true);
        document.addEventListener("touchstart", retry, true);
        // eslint-disable-next-line no-console
        console.info("[Isaiah tour] audio queued, will play on first click/tap:", playErr?.message);
      }
    } catch (e) {
      if (myToken !== speakTokenRef.current) return;  // don't fire fallback for stale speak
      // POLICY (2026-05-14): NEVER fall back to window.speechSynthesis.
      // The browser's built-in TTS picks a system voice (Samantha, Karen,
      // Google US English, etc.) which is NOT Mr. Lawrence. Users reported
      // hearing those system voices on production whenever ElevenLabs and
      // the OpenAI Onyx fallback both failed. Mr. Lawrence is the ONE
      // sanctioned voice; we'd rather be silent than wrong.
      // The console line below preserves debuggability without making sound.
      // eslint-disable-next-line no-console
      console.warn("[Isaiah tour] backend TTS failed, staying silent (no browser-voice fallback):", e?.message || e);
    }
  }, [voiceEnabled]);

  const stopSpeaking = useCallback(() => {
    // Bumping the token here tells any in-flight speak() awaiters to bail out
    // before they hit audio.play(), kills the "two voices overlapping" race.
    speakTokenRef.current++;
    try {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.src = "";
      }
      if ("speechSynthesis" in window) window.speechSynthesis.cancel();
    } catch {}
    try { if (window.__iw_voice_lock === "platform-tour") delete window.__iw_voice_lock; } catch (_) {}
  }, []);

  const toggleVoice = useCallback(() => {
    setVoiceEnabled(v => {
      const nv = !v;
      try { localStorage.setItem("isaiah_tour_voice", nv ? "on" : "off"); } catch {}
      if (!nv) stopSpeaking();
      return nv;
    });
  }, [stopSpeaking]);

  // Fetch the per-account "seen" flag once we know who's logged in. This is the
  // authoritative gate; localStorage is only a same-device fast-path.
  useEffect(() => {
    if (!isAuthenticated) { setServerSeen(null); return; }
    let alive = true;
    const key = user?.id || user?.email || null;
    axios.get(`${API}/tour/seen`, { params: key ? { user_key: key } : {}, withCredentials: true })
      .then((r) => { if (alive) setServerSeen(!!r.data?.seen); })
      .catch(() => { if (alive) setServerSeen(false); });
    return () => { alive = false; };
  }, [isAuthenticated, user?.id, user?.email]);

  // Auto-start on first visit (only on homepage), delay lets the page paint
  // and gives the user time to interact (unlocking browser autoplay).
  // CRITICAL: do NOT auto-start while the post-signup Welcome modal is on
  // screen, otherwise Isaiah's tour voice plays behind the modal and the user
  // hears narration about a page they can't see.
  useEffect(() => {
    if (previewMode) {
      // Admin preview, force-start regardless of enabled/completed gates.
      if (!isHomePage) return;
      const t = setTimeout(() => { setStep(0); setActive(true); }, 600);
      return () => clearTimeout(t);
    }
    if (!isAuthenticated) return;
    if (!tourEnabled) return;
    // Account says already seen → never auto-start. Wait for the server answer
    // (serverSeen === null means "still loading"); only start when it's false.
    if (serverSeen !== false) return;
    const completed = localStorage.getItem(STORAGE_KEY);
    if (completed) return;

    // The Welcome Tour must appear IMMEDIATELY on a user's very first entry —
    // on whatever page they landed on after signup. The spotlight targets live
    // on the homepage, so bring a first-time user home ONCE so the tour runs
    // right away (instead of only firing whenever they later tap "Home").
    if (!isHomePage) {
      if (!_autoNavNudged) { _autoNavNudged = true; navigate("/"); }
      return;
    }

    // Persist "seen" the MOMENT the tour auto-opens (not only on finish/skip),
    // so it can NEVER auto-trigger a second time — even if the user navigates
    // away mid-tour. They can always replay it from the profile dropdown.
    const markSeenNow = () => {
      try { localStorage.setItem(STORAGE_KEY, "true"); } catch { /* ignore */ }
      try {
        const key = user?.id || user?.email || null;
        axios.post(`${API}/tour/seen`, key ? { user_key: key } : {}, { withCredentials: true }).catch(() => {});
      } catch { /* non-fatal */ }
    };

    let cancelled = false;
    const tryStart = () => {
      if (cancelled) return;
      // Check every 250ms: while the welcome modal is mounted (or kid
      // onboarding is active), defer auto-start. The Welcome modal sets
      // `isaiah_welcome_open` in sessionStorage on mount and clears it on
      // dismiss; KidOnboarding uses `isaiah_kid_onboarding_active`.
      const blocked = sessionStorage.getItem("isaiah_welcome_open") === "1"
        || sessionStorage.getItem("isaiah_kid_onboarding_active") === "1"
        || document.querySelector('[data-testid="welcome-new-user-modal"]');
      if (blocked) {
        setTimeout(tryStart, 400);
        return;
      }
      setActive(true);
      markSeenNow();
    };
    const timer = setTimeout(tryStart, 2800);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [isHomePage, isAuthenticated, tourEnabled, previewMode, serverSeen]);

  // AUTOPLAY PRIMER: on first render, attach a one-shot listener to the document
  // that primes an <audio> element on the user's FIRST gesture (click, tap, key).
  // This creates audio-context permission BEFORE the tour tries to speak, so by
  // the time the 2.8s delay finishes, audio will play without the mute-toggle
  // workaround. Silent MP3 keeps the prime inaudible.
  useEffect(() => {
    const PRIMED = "__isaiah_audio_primed__";
    if (window[PRIMED]) return;
    const primer = () => {
      if (window[PRIMED]) return;
      window[PRIMED] = true;
      try {
        const a = new Audio("data:audio/mp3;base64,SUQzBAAAAAABEVRYWFgAAAAtAAADY29tbWVudABCaWdTb3VuZEJhbmsuY29tIC8gTGFTb25vdGhlcXVlLm9yZwBURU5DAAAAHQAAA1N3aXRjaCBQbHVzIMKpIE5DSCBTb2Z0d2FyZQBUSVQyAAAABgAAAzIyMzUAVFNTRQAAAA8AAANMYXZmNTcuODMuMTAwAAAAAAAAAAAAAAD/80DEAAAAA0gAAAAATEFNRTMuMTAwVVVVVVVVVVVVVUxBTUUzLjEwMFVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVV");
        a.volume = 0; a.play().then(() => a.pause()).catch(() => {});
      } catch {}
      document.removeEventListener("pointerdown", primer, true);
      document.removeEventListener("keydown", primer, true);
      document.removeEventListener("touchstart", primer, true);
    };
    document.addEventListener("pointerdown", primer, true);
    document.addEventListener("keydown", primer, true);
    document.addEventListener("touchstart", primer, true);
    return () => {
      document.removeEventListener("pointerdown", primer, true);
      document.removeEventListener("keydown", primer, true);
      document.removeEventListener("touchstart", primer, true);
    };
  }, []);

  // PRE-WARM: as soon as the tour becomes active, hit the TTS endpoint for the
  // first step in the background so when the user clicks anything (which unlocks
  // autoplay) the audio is already cached and plays instantly.
  useEffect(() => {
    if (!active || step !== 0) return;
    const first = mergedSteps[0];
    if (!first) return;
    const text = `${first.title}. ${first.message}`;
    if (audioCacheRef.current.get(text)) return;
    (async () => {
      try {
        const r = await axios.post(`${API}/tts/tour`, { text }, { timeout: 15000 });
        if (r.data?.audio_url) audioCacheRef.current.set(text, r.data.audio_url);
      } catch { /* non-fatal */ }
    })();
  }, [active, step]);

  const findAndMeasureElements = useCallback((selectors) => {
    const rects = [];
    for (const sel of selectors) {
      // Use querySelectorAll and find the first VISIBLE element (non-zero size)
      const allEls = document.querySelectorAll(sel);
      for (const el of allEls) {
        const rect = el.getBoundingClientRect();
        if (rect.width > 0 && rect.height > 0) {
          rects.push(rect);
          break;
        }
      }
    }
    return rects;
  }, []);

  // Update target rects when step changes
  const updateRect = useCallback(() => {
    if (!active || step >= mergedSteps.length) return;
    const currentStep = mergedSteps[step];
    const isMobile = window.innerWidth < 768;
    const selectors = (isMobile && currentStep.mobileTargets) ? currentStep.mobileTargets : currentStep.targets;

    const doMeasure = () => {
      const rects = findAndMeasureElements(selectors);
      setTargetRects(rects);
      setIsTransitioning(false);
    };

    if (currentStep.scrollTo === "world-map") {
      const isMobile = window.innerWidth < 768;
      if (isMobile) {
        // MOBILE FIX: the top nav bar is no longer on screen, so scrolling the
        // whole map to the top tucked the first nodes (Learning Forest,
        // Adventure Island) up behind the sticky header — the spotlight then
        // framed the logo/header instead of the node. Center the ACTUAL target
        // node in the viewport so it's always fully visible below the header.
        const targetEl = findAndMeasureElements(selectors).length
          ? (document.querySelector(selectors.find((s) => {
              const e = document.querySelector(s);
              return e && e.getBoundingClientRect().width > 0;
            })) )
          : null;
        if (targetEl) {
          targetEl.scrollIntoView({ behavior: "smooth", block: "center" });
          setTimeout(doMeasure, 650);
        } else {
          const worldMap = document.querySelector('[data-testid="world-map-mobile"]') || document.querySelector('[data-testid="world-map"]');
          if (worldMap) {
            const mapTop = worldMap.getBoundingClientRect().top + window.scrollY;
            window.scrollTo({ top: Math.max(0, mapTop - 60), behavior: "smooth" });
          }
          setTimeout(doMeasure, 800);
        }
      } else {
        const worldMap = document.querySelector('[data-testid="world-map"]');
        if (worldMap) {
          const mapTop = worldMap.getBoundingClientRect().top + window.scrollY;
          window.scrollTo({ top: Math.max(0, mapTop - 100), behavior: "smooth" });
          setTimeout(doMeasure, 800);
        } else {
          window.scrollTo({ top: 600, behavior: "smooth" });
          setTimeout(doMeasure, 800);
        }
      }
    } else if (currentStep.scrollTo === "element") {
      // Scroll to the first target element
      const el = document.querySelector(currentStep.targets[0]);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        setTimeout(doMeasure, 600);
      } else {
        setTimeout(doMeasure, 600);
      }
    } else if (typeof currentStep.scrollTo === "number") {
      window.scrollTo({ top: currentStep.scrollTo, behavior: "smooth" });
      setTimeout(doMeasure, 600);
    } else {
      setTimeout(doMeasure, 300);
    }
  }, [active, step, findAndMeasureElements, mergedSteps]);

  useEffect(() => {
    if (active) {
      setIsTransitioning(true);
      updateRect();
      // Narrate the step
      const cur = mergedSteps[step];
      if (cur) speak(`${cur.title}. ${cur.message}`);
    } else {
      stopSpeaking();
    }
    // NOTE: deliberately exclude `updateRect`, `speak`, `stopSpeaking` from
    // deps, they are useCallback identities that can change without any
    // semantic change to this effect, and including them caused the tour
    // speak() to fire twice in StrictMode (the 2-voice "echo" the user
    // reported on the homepage).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, step]);

  // Handle resize
  useEffect(() => {
    if (!active) return;
    const handler = () => updateRect();
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, [active, updateRect]);

  // Re-measure the spotlight on scroll (debounced, NO re-scroll). Fixes the
  // "spotlight lands on the wrong area / bleeds into the next section" bug:
  // the initial measure can happen before a smooth-scroll settles, leaving a
  // stale rect. This keeps the highlight glued to the real target once the
  // page stops moving.
  useEffect(() => {
    if (!active) return;
    let raf = null;
    const measureOnly = () => {
      if (step >= mergedSteps.length) return;
      const cur = mergedSteps[step];
      const isMobile = window.innerWidth < 768;
      const selectors = (isMobile && cur.mobileTargets) ? cur.mobileTargets : cur.targets;
      setTargetRects(findAndMeasureElements(selectors));
    };
    const onScroll = () => {
      if (raf) cancelAnimationFrame(raf);
      raf = requestAnimationFrame(measureOnly);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [active, step, findAndMeasureElements, mergedSteps]);

  const nextStep = () => {
    if (step < mergedSteps.length - 1) {
      setIsTransitioning(true);
      setStep(s => s + 1);
    } else {
      endTour(true);
    }
  };

  const prevStep = () => {
    if (step > 0) {
      setIsTransitioning(true);
      setStep(s => s - 1);
    }
  };

  const endTour = (showModal = false) => {
    stopSpeaking();
    setActive(false);
    setStep(0);
    setTargetRects([]);
    // In admin preview we do NOT mark the real tour completed, just clean up
    // the preview flag so the normal tour behavior resumes on next load.
    if (previewMode) {
      try { sessionStorage.removeItem("iw_tour_preview"); } catch { /* ignore */ }
      setPreviewMode(false);
    } else {
      localStorage.setItem(STORAGE_KEY, "true");
      // Lock it in on the ACCOUNT so it never re-triggers on another device or
      // after clearing storage.
      setServerSeen(true);
      try {
        const key = user?.id || user?.email || null;
        axios.post(`${API}/tour/seen`, key ? { user_key: key } : {}, { withCredentials: true }).catch(() => {});
      } catch { /* non-fatal */ }
    }
    // Always return the user to the top of the page so they land where they
    // started (Isaiah avatar + header in view). Without this, finishing the
    // tour leaves them mid-scroll wherever the last highlighted target was.
    try {
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch { /* SSR / older browsers */ }
    // Only invite unauthenticated visitors to sign up, logged-in users
    // already have an account, so the "Ready to Join the Universe?" CTA
    // would be confusing and ask them to log out + sign up again.
    if (showModal && !isAuthenticated) {
      setShowCTA(true);
    }
  };

  const startTour = () => {
    if (!isHomePage) {
      navigate("/");
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: "smooth" });
        setTimeout(() => { setStep(0); setActive(true); }, 500);
      }, 500);
    } else {
      window.scrollTo({ top: 0, behavior: "smooth" });
      setTimeout(() => { setStep(0); setActive(true); }, 400);
    }
  };

  // Launch the tour from anywhere (e.g. the profile dropdown "Take a Tour" link)
  // now that the floating launcher button has been removed.
  useEffect(() => {
    const handler = () => startTour();
    window.addEventListener("isaiah:start-tour", handler);
    return () => window.removeEventListener("isaiah:start-tour", handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isHomePage]);

  // Calculate tooltip position based on available rects
  const getTooltipStyle = () => {
    const isMobile = window.innerWidth < 768;
    const tooltipWidth = isMobile ? window.innerWidth - 40 : 380;

    if (!targetRects || targetRects.length === 0) {
      return { top: "50%", left: "50%", transform: "translate(-50%, -50%)" };
    }

    const currentStep = mergedSteps[step];
    const pos = currentStep.position;
    const gap = 16;

    // For dual-spotlight steps, position near the world node (last rect on desktop, first on mobile)
    const mainRect = isMobile ? targetRects[0] : (targetRects.length > 1 ? targetRects[targetRects.length - 1] : targetRects[0]);

    // Mobile: always center horizontally, position below/above main target
    if (isMobile) {
      const topPos = mainRect.bottom + gap;
      const maxTop = window.innerHeight - 300; // leave room for tooltip + bottom nav
      return {
        top: `${Math.min(topPos, maxTop)}px`,
        left: "20px",
        right: "20px",
        width: "auto",
      };
    }

    if (pos === "bottom") {
      return {
        top: `${mainRect.bottom + gap}px`,
        left: `${Math.max(20, Math.min(window.innerWidth - tooltipWidth - 20, mainRect.left + mainRect.width / 2 - tooltipWidth / 2))}px`,
      };
    }
    if (pos === "top") {
      return {
        top: `${Math.max(20, mainRect.top - gap - 240)}px`,
        left: `${Math.max(20, Math.min(window.innerWidth - tooltipWidth - 20, mainRect.left + mainRect.width / 2 - tooltipWidth / 2))}px`,
      };
    }
    if (pos === "center-right") {
      return {
        top: `${Math.max(80, mainRect.top + mainRect.height / 2 - 100)}px`,
        left: `${Math.min(window.innerWidth - tooltipWidth - 20, mainRect.right + gap)}px`,
      };
    }
    if (pos === "center-left") {
      return {
        top: `${Math.max(80, mainRect.top + mainRect.height / 2 - 100)}px`,
        left: `${Math.max(20, mainRect.left - gap - tooltipWidth)}px`,
      };
    }
    // "center", position centered below the world node
    return {
      top: `${Math.min(window.innerHeight - 280, mainRect.bottom + gap)}px`,
      left: `${Math.max(20, Math.min(window.innerWidth - tooltipWidth - 20, mainRect.left + mainRect.width / 2 - tooltipWidth / 2))}px`,
    };
  };

  const currentStep = mergedSteps[step];

  return (
    <>
      {/* The floating "Take a Tour" launcher was removed (it blocked content,
          especially on mobile). The tour is now started from the profile
          dropdown, which dispatches the "isaiah:start-tour" window event. */}

      {/* Active Tour */}
      {active && isAuthenticated && (
        <>
          <SpotlightOverlay rects={targetRects} onClick={() => {}} />

          {/* Tooltip */}
          <div ref={tooltipRef}
            className="fixed z-[9999] w-[380px] max-w-[calc(100vw-40px)]"
            style={{
              ...getTooltipStyle(),
              opacity: isTransitioning ? 0 : 1,
              transform: `${getTooltipStyle().transform || ""} ${isTransitioning ? "translateY(10px)" : "translateY(0)"}`,
              transition: "opacity 0.3s ease, transform 0.3s ease",
            }}>
            <div className="rounded-2xl p-5 border-2 border-purple-400/30"
              style={{ background: "linear-gradient(135deg, rgba(30,20,60,0.95), rgba(20,15,50,0.95))", boxShadow: "0 0 40px rgba(139,92,246,0.2), 0 20px 40px rgba(0,0,0,0.3)" }}>

              {/* Isaiah + Title */}
              <div className="flex items-start gap-3 mb-3">
                <div className="shrink-0 w-14 h-16 relative">
                  <IsaiahMascotCore
                    pose={currentStep.pose}
                    size="icon"
                    style={{ width: 56, height: 64 }}
                    testId="tour-isaiah-mascot"
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between mb-1">
                    <h3 className="text-white font-fredoka font-bold text-base">{currentStep.title}</h3>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={toggleVoice}
                        className={`p-1 rounded-md transition ${voiceEnabled ? "text-cyan-300 bg-cyan-500/10 hover:bg-cyan-500/20" : "text-white/30 hover:text-white/60"}`}
                        data-testid="tour-voice-toggle"
                        aria-label={voiceEnabled ? "Mute narration" : "Unmute narration"}
                        title={voiceEnabled ? "Mute Isaiah's voice" : "Let Isaiah read aloud"}
                      >
                        {voiceEnabled
                          ? (<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 5 6 9H2v6h4l5 4z"/><path d="M15.54 8.46a5 5 0 0 1 0 7.07"/><path d="M19.07 4.93a10 10 0 0 1 0 14.14"/></svg>)
                          : (<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 5 6 9H2v6h4l5 4z"/><line x1="22" y1="9" x2="16" y2="15"/><line x1="16" y1="9" x2="22" y2="15"/></svg>)}
                      </button>
                      <button onClick={() => endTour(false)} className="text-white/30 hover:text-white/60 transition-colors p-0.5" data-testid="tour-close-btn">
                        <X size={16} />
                      </button>
                    </div>
                  </div>
                  {/* Speech bubble */}
                  <div className="relative px-3 py-2.5 rounded-xl bg-white/8 border border-white/10">
                    <p className="text-white/70 text-xs font-nunito leading-relaxed">{currentStep.message}</p>
                  </div>
                </div>
              </div>

              {/* Dual spotlight hint */}
              {targetRects.length > 1 && (
                <div className="flex items-center gap-2 mb-2 px-1">
                  <div className="flex items-center gap-1">
                    <div className="w-2 h-2 rounded-full bg-purple-400 animate-pulse" />
                    <span className="text-purple-300/70 text-[10px] font-nunito">Menu link above</span>
                  </div>
                  <span className="text-white/20 text-[10px]">+</span>
                  <div className="flex items-center gap-1">
                    <div className="w-2 h-2 rounded-full bg-purple-400 animate-pulse" />
                    <span className="text-purple-300/70 text-[10px] font-nunito">World map below</span>
                  </div>
                </div>
              )}

              {/* Navigation */}
              <div className="flex items-center justify-between mt-3 pt-3 border-t border-white/8">
                <div className="flex items-center gap-1.5">
                  {mergedSteps.map((_, i) => (
                    <div key={i}
                      className={`rounded-full transition-all ${i === step ? "w-5 h-2 bg-purple-400" : i < step ? "w-2 h-2 bg-purple-400/40" : "w-2 h-2 bg-white/15"}`} />
                  ))}
                </div>
                <div className="flex items-center gap-2">
                  {step > 0 && (
                    <button onClick={prevStep}
                      className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-white/50 text-xs font-bold hover:text-white/80 hover:bg-white/5 transition-all"
                      data-testid="tour-prev-btn">
                      <ChevronLeft size={12} /> Back
                    </button>
                  )}
                  <button onClick={nextStep}
                    className="flex items-center gap-1 px-4 py-1.5 rounded-lg text-xs font-fredoka font-bold transition-all"
                    style={{
                      background: step === mergedSteps.length - 1 ? "linear-gradient(135deg, #3B82F6, #8B5CF6)" : "rgba(139,92,246,0.25)",
                      color: step === mergedSteps.length - 1 ? "white" : "rgba(167,139,250,0.9)",
                      border: "1px solid rgba(139,92,246,0.35)",
                    }}
                    data-testid="tour-next-btn">
                    {step === mergedSteps.length - 1 ? (
                      <><Sparkles size={12} /> Finish Tour</>
                    ) : (
                      <>Next <ChevronRight size={12} /></>
                    )}
                  </button>
                </div>
              </div>

              {/* Skip link, high-contrast so it's easy to find */}
              <div className="text-center mt-3">
                <button onClick={() => endTour(false)} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-white/10 hover:bg-white/20 border border-white/25 text-white/90 text-xs font-fredoka font-bold transition-all hover:scale-105" data-testid="tour-skip-btn">
                  <X size={13} /> Skip Tour
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* CTA Modal after tour ends */}
      {showCTA && (
        <CTAModal
          onDismiss={() => setShowCTA(false)}
          onSignUp={() => { setShowCTA(false); navigate("/signup"); }}
          onGuestPreview={() => {
            setShowCTA(false);
            // Enter REAL guest mode (5-min preview countdown, no pseudo-login)
            loginAsDemo();
            navigate("/");
          }}
        />
      )}
    </>
  );
};

export default PlatformTour;
